// src/services/trainingApi.ts
import { apiSlice } from "@/redux/apiSlice";
import {
  TrainingPackage,
  TrainingPackageCreate,
  TrainingPackageUpdate,
  ClientTrainingSeries,
  TrainingSession,
  GenerateSchedulePayload,
  ReschedulePayload,
  GetAllResponse,
} from "@/types/training";

export const trainingApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPackages: builder.query<
      GetAllResponse<TrainingPackage>,
      {
        org_id?: number;
        status?: string;
        training_type?: string;
        coach_id?: number;
        facility_id?: number;
        search?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.status && params.status !== "all") {
          queryParams.append("status", params.status);
        }
        if (params?.training_type && params.training_type !== "all") {
          queryParams.append("training_type", params.training_type);
        }
        if (params?.coach_id) {
          queryParams.append("coach_id", String(params.coach_id));
        }
        if (params?.facility_id) {
          queryParams.append("facility_id", String(params.facility_id));
        }
        if (params?.search) {
          queryParams.append("search", params.search);
        }
        if (params?.limit) {
          queryParams.append("limit", String(params.limit));
        }
        if (params?.offset !== undefined) {
          queryParams.append("offset", String(params.offset));
        }

        const qs = queryParams.toString();
        return {
          url: `/training/package${qs ? `?${qs}` : ""}`,
          method: "GET",
          headers: { Accept: "application/json" },
        };
      },
      providesTags: ["TrainingPackage"],
    }),

    getPackageById: builder.query<TrainingPackage, number>({
      query: (id) => ({
        url: `/training/package/${id}`,
        method: "GET",
        headers: { Accept: "application/json" },
      }),
      providesTags: (result, error, id) => [{ type: "TrainingPackage", id }],
    }),

    createPackage: builder.mutation<TrainingPackage, TrainingPackageCreate>({
      query: (body) => ({
        url: "/training/package",
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["TrainingPackage"],
    }),

    updatePackage: builder.mutation<
      TrainingPackage,
      { id: number; data: TrainingPackageUpdate }
    >({
      query: ({ id, data }) => ({
        url: `/training/package/${id}`,
        method: "PUT",
        body: data,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        "TrainingPackage",
        { type: "TrainingPackage", id },
      ],
    }),

    deletePackage: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/training/package/${id}`,
        method: "DELETE",
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["TrainingPackage"],
    }),

    // Series & Sessions
    getSeriesList: builder.query<
      GetAllResponse<ClientTrainingSeries>,
      {
        client_id?: number;
        coach_id?: number;
        facility_id?: number;
        training_type?: string;
        status?: string;
        training_package_id?: number;
        pos_transaction_id?: number;
        search?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.client_id) queryParams.append("client_id", String(params.client_id));
        if (params?.coach_id) queryParams.append("coach_id", String(params.coach_id));
        if (params?.facility_id) queryParams.append("facility_id", String(params.facility_id));
        if (params?.training_type && params.training_type !== "all") queryParams.append("training_type", params.training_type);
        if (params?.status && params.status !== "all") queryParams.append("status", params.status);
        if (params?.training_package_id) queryParams.append("training_package_id", String(params.training_package_id));
        if (params?.pos_transaction_id) queryParams.append("pos_transaction_id", String(params.pos_transaction_id));
        if (params?.search) queryParams.append("search", params.search);
        if (params?.limit) queryParams.append("limit", String(params.limit));
        if (params?.offset !== undefined) queryParams.append("offset", String(params.offset));

        const qs = queryParams.toString();
        return {
          url: `/training/series${qs ? `?${qs}` : ""}`,
          method: "GET",
          headers: { Accept: "application/json" },
        };
      },
      providesTags: ["TrainingSeries"],
    }),

    getSeriesById: builder.query<ClientTrainingSeries, number>({
      query: (id) => ({
        url: `/training/series/${id}`,
        method: "GET",
        headers: { Accept: "application/json" },
      }),
      providesTags: (result, error, id) => [{ type: "TrainingSeries", id }],
    }),

    getSessionById: builder.query<TrainingSession, number>({
      query: (id) => ({
        url: `/training/session/${id}`,
        method: "GET",
        headers: { Accept: "application/json" },
      }),
      providesTags: (result, error, id) => [{ type: "TrainingSeries", id }],
    }),

    generateSchedule: builder.mutation<
      ClientTrainingSeries,
      { seriesId: number; data: GenerateSchedulePayload }
    >({
      query: ({ seriesId, data }) => ({
        url: `/training/series/${seriesId}/generate-schedule`,
        method: "POST",
        body: data,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["TrainingSeries", "Facility"],
    }),

    rescheduleSession: builder.mutation<
      TrainingSession,
      { sessionId: number; data: ReschedulePayload }
    >({
      query: ({ sessionId, data }) => ({
        url: `/training/session/${sessionId}/reschedule`,
        method: "PUT",
        body: data,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["TrainingSeries", "Facility"],
    }),

    cancelSessionSlot: builder.mutation<TrainingSession, number>({
      query: (sessionId) => ({
        url: `/training/session/${sessionId}/cancel-slot`,
        method: "PUT",
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["TrainingSeries", "Facility"],
    }),

    bookSessionSlot: builder.mutation<
      TrainingSession,
      {
        sessionId: number;
        data: { session_date: string; start_time: string; end_time: string };
      }
    >({
      query: ({ sessionId, data }) => ({
        url: `/training/session/${sessionId}/book-slot`,
        method: "POST",
        body: data,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["TrainingSeries", "Facility"],
    }),

    assignSeriesMember: builder.mutation<
      ClientTrainingSeries,
      { seriesId: number; clientId: number }
    >({
      query: ({ seriesId, clientId }) => ({
        url: `/training/series/${seriesId}/assign-member`,
        method: "PUT",
        body: { client_id: clientId },
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["TrainingSeries"],
    }),
  }),
});

export const {
  useGetPackagesQuery,
  useGetPackageByIdQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
  useGetSeriesListQuery,
  useGetSeriesByIdQuery,
  useGetSessionByIdQuery,
  useGenerateScheduleMutation,
  useRescheduleSessionMutation,
  useCancelSessionSlotMutation,
  useBookSessionSlotMutation,
  useAssignSeriesMemberMutation,
} = trainingApi;